import { Section } from 'api/rest/sections/sections.types';
import { NodeSelection, OnGoInitializeFormValues, SectionSelection, SubsectionSelection } from './onGoInitialize.types';

type Scope = OnGoInitializeFormValues['scope'];

const getSelectionState = (values: boolean[]): NodeSelection => {
  if (values.every(Boolean)) {
    return 'checked';
  }

  if (values.every(value => !value)) {
    return 'unchecked';
  }

  return 'indeterminate';
};

const getNodeSelectionState = (values: NodeSelection[]): NodeSelection => {
  if (values.every(value => value === 'checked')) {
    return 'checked';
  }

  if (values.every(value => value === 'unchecked')) {
    return 'unchecked';
  }

  return 'indeterminate';
};

export const checkSectionState = (subsections: SubsectionSelection): NodeSelection => {
  return getSelectionState(Object.values(subsections));
};

export const checkAllSectionsState = (sections: SectionSelection): NodeSelection => {
  return getNodeSelectionState(Object.values(sections).map(section => section.allSelected));
};

/** True if at least one subsection leaf is selected, or a section with no subsections is fully selected. */
export const hasAtLeastOneSubsectionSelected = (scope: Scope): boolean => {
  for (const section of Object.values(scope.sections)) {
    if (Object.values(section.subsections).some(Boolean)) {
      return true;
    }
    if (Object.keys(section.subsections).length === 0 && section.allSelected === 'checked') {
      return true;
    }
  }
  return false;
};

const parseSctionsToScope = (sectionsData: Section[]): Scope => {
  return {
    allSelected: 'checked',
    sections: Object.fromEntries(
      sectionsData.map(section => [
        section.id,
        { allSelected: 'checked', subsections: Object.fromEntries(section.subsections.map(subsection => [subsection.id, true])) },
      ]),
    ),
  };
};

export const prepareDefaultScope = (sectionsData: Section[]): Scope => {
  return parseSctionsToScope(sectionsData);
};

export const changeAllSectionsState = (scope: Scope, newState: boolean): Scope => {
  const nodeSelection = newState ? 'checked' : 'unchecked';
  return {
    allSelected: nodeSelection,
    sections: Object.keys(scope.sections).reduce((acc, sectionId) => {
      acc[sectionId] = {
        allSelected: nodeSelection,
        subsections: Object.keys(scope.sections[sectionId].subsections).reduce((acc, subsectionId) => {
          acc[subsectionId] = newState;
          return acc;
        }, {} as SubsectionSelection),
      };
      return acc;
    }, {} as SectionSelection),
  };
};

export const changeSectionState = ({ scope, sectionId, newState }: { scope: Scope; sectionId: string; newState: boolean }): Scope => {
  const sections = {
    ...scope.sections,
    [sectionId]: {
      allSelected: newState ? 'checked' : 'unchecked',
      subsections: Object.keys(scope.sections[sectionId].subsections).reduce((acc, subsectionId) => {
        acc[subsectionId] = newState;
        return acc;
      }, {} as SubsectionSelection),
    },
  } as SectionSelection;
  return {
    allSelected: checkAllSectionsState(sections),
    sections,
  };
};

export const changeSubsectionState = ({
  scope,
  sectionId,
  subsectionId,
  newState,
}: {
  scope: Scope;
  sectionId: string;
  subsectionId: string;
  newState: boolean;
}): Scope => {
  const subsections = {
    ...scope.sections[sectionId].subsections,
    [subsectionId]: newState,
  };
  const sections = {
    ...scope.sections,
    [sectionId]: {
      allSelected: checkSectionState(subsections),
      subsections,
    },
  } as SectionSelection;

  return {
    allSelected: checkAllSectionsState(sections),
    sections,
  };
};
